import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Video, Play, CheckCircle, Sparkles, Loader2 } from 'lucide-react';
import axios from 'axios';
import SITE_CONFIG from '../config/siteConfig';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const VideoTemplatesPage = () => {
  const navigate = useNavigate();
  const [carousels, setCarousels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedCarousel, setSelectedCarousel] = useState(null);

  // Video template options
  const videoTemplates = [
    {
      id: 'modern-fade',
      name: 'Modern Fade',
      description: 'Smooth fade transitions with elegant text overlays',
      thumbnail: '/templates/modern-fade.jpg',
      duration: '4s per slide',
      transition: 'Fade',
      features: ['Text Overlay', 'Brand Colors', 'Smooth Transitions']
    },
    {
      id: 'dynamic-slide',
      name: 'Dynamic Slide',
      description: 'Energetic slide animations with bold typography',
      thumbnail: '/templates/dynamic-slide.jpg',
      duration: '3s per slide',
      transition: 'Slide',
      features: ['Bold Text', 'Fast Paced', 'Eye-catching']
    },
    {
      id: 'minimal-zoom',
      name: 'Minimal Zoom',
      description: 'Subtle zoom effects with clean design',
      thumbnail: '/templates/minimal-zoom.jpg',
      duration: '5s per slide',
      transition: 'Zoom',
      features: ['Clean Design', 'Subtle Motion', 'Professional']
    },
    {
      id: 'creative-wipe',
      name: 'Creative Wipe',
      description: 'Creative wipe transitions with animated elements',
      thumbnail: '/templates/creative-wipe.jpg',
      duration: '4s per slide',
      transition: 'Wipe',
      features: ['Creative', 'Animated Elements', 'Unique Style']
    }
  ];

  useEffect(() => {
    fetchCarousels();
  }, []);

  const fetchCarousels = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/carousels/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const carouselData = response.data.results || response.data;
      setCarousels(carouselData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching carousels:', err);
      setLoading(false);
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
  };

  const handleCarouselSelect = (carousel) => {
    setSelectedCarousel(carousel);
  };

  const handleGenerateVideo = async () => {
    if (!selectedTemplate || !selectedCarousel) {
      alert('Please select both a template and a carousel');
      return;
    }

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      
      const response = await axios.post(
        `${API_BASE_URL}/carousels/${selectedCarousel.id}/render-video/`,
        {
          platform: 'linkedin',
          template: selectedTemplate.id,
          duration_per_slide: parseInt(selectedTemplate.duration),
          transition_type: selectedTemplate.transition.toLowerCase()
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      alert('Video generation started! You will be notified when it\'s ready.');
      navigate('/dashboard');
    } catch (err) {
      console.error('Error generating video:', err);
      alert('Failed to generate video. Please try again.');
    }
  };

  return (
    <>
      <Helmet>
        <title>Video Templates - Choose Your Style | AI Carousel</title>
        <link rel="canonical" href={`${SITE_CONFIG.siteUrl}/video-templates`} />
        <meta 
          name="description" 
          content="Choose from professional video templates to transform your carousel into engaging video content" 
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Video className="h-8 w-8 text-primary-600" />
            <h1 className="text-3xl font-bold text-gray-900">Video Templates</h1>
          </div>
          <p className="text-gray-600">
            Choose a template style and select your carousel to generate a professional video
          </p>
        </div>

        {/* Template Selection */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Step 1: Choose Your Template</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {videoTemplates.map((template) => (
              <div
                key={template.id}
                onClick={() => handleTemplateSelect(template)}
                className={`card cursor-pointer transition-all hover:shadow-lg ${
                  selectedTemplate?.id === template.id
                    ? 'ring-2 ring-primary-600 shadow-lg'
                    : ''
                }`}
              >
                {/* Template Thumbnail */}
                <div className="relative h-48 bg-gradient-to-br from-primary-100 to-primary-200 rounded-t-lg flex items-center justify-center">
                  <Play className="h-16 w-16 text-primary-600 opacity-50" />
                  {selectedTemplate?.id === template.id && (
                    <div className="absolute top-3 right-3">
                      <CheckCircle className="h-6 w-6 text-primary-600 fill-white" />
                    </div>
                  )}
                </div>

                {/* Template Info */}
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {template.name}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {template.description}
                  </p>

                  {/* Features */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium mr-2">Duration:</span>
                      {template.duration}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium mr-2">Transition:</span>
                      {template.transition}
                    </div>
                  </div>

                  {/* Feature Tags */}
                  <div className="flex flex-wrap gap-2">
                    {template.features.map((feature, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-primary-50 text-primary-700 text-xs rounded-full"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Carousel Selection */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Step 2: Select Your Carousel</h2>

          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
              <span className="ml-2 text-gray-600">Loading carousels...</span>
            </div>
          ) : carousels.length === 0 ? (
            <div className="card p-8 text-center">
              <p className="text-gray-600 mb-4">You don't have any carousels yet.</p>
              <Link to="/generate" className="btn-primary">
                Create Your First Carousel
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {carousels.map((carousel) => (
                <div
                  key={carousel.id}
                  onClick={() => handleCarouselSelect(carousel)}
                  className={`card cursor-pointer transition-all hover:shadow-lg ${
                    selectedCarousel?.id === carousel.id
                      ? 'ring-2 ring-primary-600 shadow-lg'
                      : ''
                  }`}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {carousel.title}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {carousel.slide_count || 0} slides
                        </p>
                      </div>
                      {selectedCarousel?.id === carousel.id && (
                        <CheckCircle className="h-6 w-6 text-primary-600 flex-shrink-0" />
                      )}
                    </div>

                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span>{carousel.platform}</span>
                      <span>{new Date(carousel.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Generate Button */}
        <div className="card p-6 bg-gradient-to-r from-primary-50 to-purple-50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Ready to Generate Your Video?
              </h3>
              <p className="text-sm text-gray-600">
                {selectedTemplate && selectedCarousel ? (
                  <>
                    Creating video with <span className="font-medium">{selectedTemplate.name}</span> template
                    for <span className="font-medium">{selectedCarousel.title}</span>
                  </>
                ) : (
                  'Select a template and carousel to continue'
                )}
              </p>
            </div>
            <button
              onClick={handleGenerateVideo}
              disabled={!selectedTemplate || !selectedCarousel}
              className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Sparkles className="h-5 w-5" />
              Generate Video
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default VideoTemplatesPage;
